import React, { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveAuthToken, getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { getOrderDeliveryConfirmation } from '@/api/deliveries';
import { loadAuthenticatedMedia } from '@/api/authenticatedMedia';
import { API } from '@/config/constants';
import ProofOfDelivery from '@/components/ProofOfDelivery';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

const object = (value) => !!value && typeof value === 'object' && !Array.isArray(value);
const optionalText = (value) => value == null || typeof value === 'string';
const optionalTime = (value) => value == null || (typeof value === 'string' && Number.isFinite(Date.parse(value)));

// Only render validated server proof, never a cached run snapshot or phone copy.
export function validHistoryConfirmation(result, orderId) {
  const proof = result?.confirmation;
  if (result?.order_id !== orderId || typeof result.status !== 'string' || !object(proof)
    || typeof proof.received_by !== 'string'
    || ['notes', 'received_by_relationship', 'confirmed_by_user_name', 'manually_recorded_by'].some((key) => !optionalText(proof[key]))
    || ['time_delivered', 'time_confirmed', 'confirmed_at'].some((key) => !optionalTime(proof[key]))
    || (proof.manual_entry != null && typeof proof.manual_entry !== 'boolean')) return false;
  if (proof.gps_location != null) {
    const gps = proof.gps_location;
    if (!object(gps) || !Number.isFinite(gps.latitude) || Math.abs(gps.latitude) > 90
      || !Number.isFinite(gps.longitude) || Math.abs(gps.longitude) > 180
      || (gps.accuracy != null && (!Number.isFinite(gps.accuracy) || gps.accuracy < 0))) return false;
  }
  if (proof.media_files != null && (!Array.isArray(proof.media_files)
    || proof.media_files.some((file) => !object(file) || !['photo', 'signature', 'video'].includes(file.file_type)
      || typeof file.storage_path !== 'string' || !file.storage_path.trim())
    || new Set(proof.media_files.map((file) => file.storage_path)).size !== proof.media_files.length)) return false;
  return true;
}

const readFailure = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return 'Access to this delivery confirmation was refused.';
  if (status === 404 && error.response?.data?.detail === 'No delivery confirmation found') return 'No saved server confirmation was found. A copy on this device does not establish server acceptance.';
  if (status === 404) return 'This order is no longer available to view.';
  return 'The confirmation could not be loaded. Check your connection and retry this read.';
};

function HistoricalProof({ order, token, owns }) {
  const [media, setMedia] = useState({});
  const controls = useRef(null);
  useEffect(() => {
    let active = true;
    const requests = new Map();
    const urls = new Map();
    const files = order.delivery_confirmation.media_files || [];
    const paths = new Set(files.map((file) => file.storage_path));
    const current = () => active && owns();
    const revoke = (path) => {
      const url = urls.get(path);
      if (url) URL.revokeObjectURL(url);
      urls.delete(path);
    };
    const load = async (path) => {
      if (!current() || !paths.has(path) || requests.has(path)) return;
      const controller = new AbortController();
      requests.set(path, controller);
      revoke(path);
      setMedia((previous) => ({ ...previous, [path]: { state: 'loading' } }));
      try {
        const result = await loadAuthenticatedMedia(`${API}/delivery-media/${encodeURIComponent(path)}`, {
          signal: controller.signal, headers: { Authorization: `Bearer ${token}` },
        });
        if (!current() || controller.signal.aborted) return;
        let next = result;
        if (result?.blob) {
          const url = URL.createObjectURL(result.blob);
          urls.set(path, url);
          next = { state: result.state, url };
        }
        setMedia((previous) => ({ ...previous, [path]: next }));
      } catch {
        if (current() && !controller.signal.aborted) setMedia((previous) => ({ ...previous, [path]: { state: 'error' } }));
      } finally { if (requests.get(path) === controller) requests.delete(path); }
    };
    controls.current = {
      load,
      fail: (path) => {
        if (!current() || !paths.has(path)) return;
        revoke(path);
        setMedia((previous) => ({ ...previous, [path]: { state: 'error' } }));
      },
    };
    paths.forEach(load);
    return () => {
      active = false;
      controls.current = null;
      requests.forEach((controller) => controller.abort());
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [order, token, owns]);
  return <ProofOfDelivery order={order} readOnly media={media}
    onRetryMedia={(path) => controls.current?.load(path)} onMediaError={(path) => controls.current?.fail(path)} />;
}

export default function DriverDeliveryHistory({ order, sessionBinding, onClose }) {
  const { sessionBinding: currentBinding } = useAuth();
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const [retry, setRetry] = useState(0);
  const [read, setRead] = useState(null);
  const latest = useRef(null);
  const opening = useMemo(() => ({ orderId: order?.id, binding: sessionBinding, token: getActiveAuthToken() }), [order?.id, sessionBinding]);
  latest.current = { opening, currentBinding };
  const owns = useMemo(() => () => latest.current?.opening === opening
    && !!opening.binding?.user?.id && opening.binding.source === 'cloud'
    && latest.current.currentBinding === opening.binding
    && getActiveSessionRevision() === opening.binding.revision, [opening]);
  const visible = owns() && revision === opening.binding.revision;

  useEffect(() => {
    const controller = new AbortController();
    if (!visible || !opening.token || !opening.orderId) return () => controller.abort();
    setRead({ opening, retry, state: 'loading' });
    getOrderDeliveryConfirmation(opening.orderId, { token: opening.token, signal: controller.signal }).then((result) => {
      if (controller.signal.aborted || !owns()) return;
      if (!validHistoryConfirmation(result, opening.orderId)) {
        setRead({ opening, retry, state: 'error', message: 'The server returned an invalid confirmation. Nothing has been changed; retry this read.' });
        return;
      }
      setRead({ opening, retry, state: 'ready', order: { id: opening.orderId, status: result.status, delivery_confirmation: result.confirmation } });
    }).catch((error) => {
      if (!controller.signal.aborted && owns()) setRead({ opening, retry, state: 'error', message: readFailure(error) });
    });
    return () => controller.abort();
  }, [opening, owns, retry, visible]);

  if (!visible) return null;
  const result = read?.opening === opening && read?.retry === retry ? read : null;
  return <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
    <DialogContent className="max-w-3xl w-[calc(100%-1rem)] p-4 sm:p-6">
      <DialogTitle className="pr-8">Delivery confirmation — view only</DialogTitle>
      <DialogDescription>
        Server record for {order.recipient_name || 'this recipient'} · Order {order.internal_order_number || opening.orderId}.
        {' '}Viewing does not send or change any saved copy on this device.
      </DialogDescription>
      {!opening.token ? <p role="alert">Sign in again to view this confirmation.</p>
        : result?.state === 'ready' ? <HistoricalProof key={retry} order={result.order} token={opening.token} owns={owns} />
          : result?.state === 'error' ? <div role="alert"><p>{result.message}</p><button type="button" className="mt-3 rounded-lg border px-4 py-2" onClick={() => { if (owns()) setRetry((value) => value + 1); }}>Retry confirmation</button></div>
            : <p role="status">Loading confirmation…</p>}
    </DialogContent>
  </Dialog>;
}
